import { EditorEvents, JSONContent } from '@tiptap/core';
import { EditorContent, useCurrentEditor } from '@tiptap/react';
import {
  useCallback,
  useEffect,
  HTMLProps,
  createContext,
  useContext,
  MutableRefObject,
} from 'react';

import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import useLatest from '../../hooks/useLatest';
import useStateDelayed from '../../hooks/useStateDelayed';
import { getContentParser, getContentSerializer } from './plugins/Content';
import AlertTooltip from './AlertTooltip';
import { debounce } from 'lodash';

type EditorRendererContextValue = {
  editorRef: MutableRefObject<HTMLDivElement>;
};

export const EditorRendererContext = createContext<EditorRendererContextValue | undefined>(
  undefined,
);

export const useEditorRendererContext = () => {
  const context = useContext(EditorRendererContext);

  if (!context) {
    throw new Error('useEditorRendererContext must be used within a EditorRendererProvider');
  }

  return context;
};

export interface EditorProps extends Omit<HTMLProps<HTMLDivElement>, 'ref'> {
  disableCopy?: boolean;
  disablePaste?: boolean;
  onDisabledCopy?: () => void;
  onDisabledPaste?: () => void;
  enableRTL?: boolean;
  content?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  onContentChange?: (content: string, timestamp: number) => void;
  onJsonChange?: (json: JSONContent) => void;
  onEditorBlur?: ({
    content,
    timestamp,
    event,
  }: {
    content: string;
    timestamp: number;
    event: FocusEvent;
  }) => void;
  onEditorFocus?: ({
    content,
    timestamp,
    event,
  }: {
    content: string;
    timestamp: number;
    event: FocusEvent;
  }) => void;
  'data-testid'?: string;
}

export default function Editor({
  className,
  content,
  placeholder,
  disabled,
  readOnly,
  disableCopy,
  disablePaste,
  onDisabledCopy,
  onDisabledPaste,
  onContentChange,
  onEditorBlur,
  onEditorFocus,
  onJsonChange,
  enableRTL = false,
  'data-testid': testId,
  ...props
}: EditorProps) {
  const [isFocus, setIsFocus] = useStateDelayed(false, 300);
  const onContentChangeRef = useLatest(onContentChange);
  const onJsonChangeRef = useLatest(onJsonChange);
  const onEditorBlurRef = useLatest(onEditorBlur);
  const onEditorFocusRef = useLatest(onEditorFocus);

  const { editor } = useCurrentEditor();

  const { editorRef } = useEditorRendererContext();

  const handleContentChange = useCallback(
    ({ editor, transaction }: EditorEvents['update']) => {
      const getContent = getContentSerializer(editor);
      if (!getContent) return;
      const time = transaction.time;

      const json = editor.getJSON();

      onJsonChangeRef.current?.(json);
      onContentChangeRef.current?.(getContent(), time);
    },
    [onContentChangeRef, onJsonChangeRef],
  );

  useEffect(
    function updateReadOnly() {
      editor?.setEditable(!disabled && !readOnly);
    },
    [editor, disabled, readOnly],
  );

  useEffect(
    function updateTextDirection() {
      if (!editor) return;

      editor.commands.setTextDirection('auto');
    },
    [editor],
  );

  useEffect(
    function updateOptions() {
      if (!editor) return;

      const onFocus = ({ editor, transaction, event }: EditorEvents['focus']) => {
        setIsFocus(true);
        const getContent = getContentSerializer(editor);

        if (!getContent) return;

        onEditorFocusRef.current?.({
          content: getContent(),
          timestamp: transaction.time,
          event,
        });

        handleContentChange({
          editor,
          transaction,
        });
      };
      const onBlur = ({ editor, transaction, event }: EditorEvents['blur']) => {
        setIsFocus(false);
        const getContent = getContentSerializer(editor);

        if (!getContent) return;

        onEditorBlurRef.current?.({
          content: getContent(),
          timestamp: transaction.time,
          event,
        });
      };

      editor.on('update', handleContentChange);
      editor.on('focus', onFocus);
      editor.on('blur', onBlur);

      return () => {
        editor.off('update', handleContentChange);
        editor.off('focus', onFocus);
        editor.off('blur', onBlur);
      };
    },
    [editor, handleContentChange, setIsFocus, onEditorBlurRef, onEditorFocusRef],
  );

  useEffect(
    // eslint-disable-next-line complexity
    function updateContent() {
      if (!editor) return;
      const parseContent = getContentParser(editor);
      const debouncedPraseContent = debounce(parseContent, 500);
      debouncedPraseContent(content, isFocus);
      return () => {
        debouncedPraseContent.cancel();
      };
    },
    [editor, content, isFocus],
  );

  useEffect(
    function updatePlaceholder() {
      if (!editor) return;

      editor.commands.updatePlaceholder(placeholder ?? '');
    },
    [editor, placeholder],
  );

  useEffect(
    function updateDisableCopyPaste() {
      if (!editor) return;

      editor.commands.updateDisableCopy(!!disableCopy);
      editor.commands.updateDisablePaste(!!disablePaste);
    },
    [editor, disableCopy, disablePaste],
  );

  useEffect(
    function updateDisableCopyPasteEventHandlers() {
      if (!editor) return;
      editor.storage.noCopyPaste.onDisabledCopy = onDisabledCopy;
      editor.storage.noCopyPaste.onDisabledPaste = onDisabledPaste;
    },
    [editor, onDisabledCopy, onDisabledPaste],
  );

  return (
    <>
      <AlertTooltip />
      <EditorContent
        {...props}
        className={twMerge(
          cn(
            'relative w-full placeholder:text-neutral-100 bg-neutral-0 px-2 py-2 rounded-t-md rounded-b-md',
            { 'cursor-not-allowed opacity-50': disabled, 'text-right': enableRTL },
            className,
          ),
        )}
        dir={enableRTL ? 'rtl' : 'ltr'}
        editor={editor}
        ref={editorRef}
        data-testid={testId ?? 'editor-content'}
      />
    </>
  );
}
